import React from "react";
import { makeStyles } from "@material-ui/core";
import { RB_PAY_SETUP_COLORS } from "../constants/rbPaySettings.constants";
import { RBPaySetupTheme } from "../utils/rbPaySettings.theme";

const C = RB_PAY_SETUP_COLORS;

const useStyles = makeStyles({
  section: {
    background: C.cardBg,
    border: `1px solid ${C.cardBorder}`,
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: 16,
    boxShadow: "0 1px 5px rgba(26,16,53,0.05)",
  },
  sectionEditing: {
    borderLeft: `3px solid ${C.primaryPurple}`,
  },
  header: {
    padding: "20px 28px 16px",
    borderBottom: `1px solid ${C.innerDivider}`,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    flex: 1,
    minWidth: 0,
  },
  badge: {
    width: 30,
    height: 30,
    minWidth: 30,
    borderRadius: 7,
    background: C.deepPurple,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 1,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "0.04em",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  title: {
    fontSize: 15.5,
    fontWeight: 700,
    color: C.textDark,
    margin: 0,
    letterSpacing: "-0.01em",
  },
  editingBadge: {
    fontSize: 10.5,
    fontWeight: 600,
    color: C.primaryPurple,
    background: C.editingBadgeBg,
    padding: "2px 9px",
    borderRadius: 4,
    flexShrink: 0,
  },
  description: {
    fontSize: 12.5,
    color: C.textMuted,
    margin: "3px 0 0",
    lineHeight: 1.4,
  },
  editButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "7px 16px",
    background: "transparent",
    color: C.primaryPurple,
    border: `1.5px solid #C8BAE8`,
    borderRadius: 7,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  body: {
    padding: "24px 28px",
  },
});

interface RBPaySetupSectionProps {
  stepNumber: string;
  title: React.ReactNode;
  description: React.ReactNode;
  isEditing?: boolean;
  showEditButton?: boolean;
  onEdit?: () => void;
  theme: RBPaySetupTheme;
  children: React.ReactNode;
}

export const RBPaySetupSection: React.FC<RBPaySetupSectionProps> = ({
  stepNumber,
  title,
  description,
  isEditing,
  showEditButton,
  onEdit,
  theme,
  children,
}) => {
  const classes = useStyles();

  return (
    <section
      className={`${classes.section} ${isEditing ? classes.sectionEditing : ""}`}
      style={isEditing ? { borderLeftColor: theme.secondary } : undefined}
    >
      <div className={classes.header}>
        <div className={classes.headerRow}>
          <div className={classes.headerLeft}>
            <div className={classes.badge} style={{ background: theme.primary }}>
              <span className={classes.badgeText}>{stepNumber}</span>
            </div>
            <div>
              <div className={classes.titleRow}>
                <h2 className={classes.title}>{title}</h2>
                {isEditing ? (
                  <span
                    className={classes.editingBadge}
                    style={{ color: theme.secondary, background: theme.secondarySoft }}
                  >
                    Editing
                  </span>
                ) : null}
              </div>
              <p className={classes.description}>{description}</p>
            </div>
          </div>
          {showEditButton ? (
            <button
              type="button"
              className={classes.editButton}
              style={{ color: theme.secondary, borderColor: theme.secondaryBorder }}
              onClick={onEdit}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path
                  d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z"
                  stroke={theme.secondary}
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Edit
            </button>
          ) : null}
        </div>
      </div>
      <div className={classes.body}>{children}</div>
    </section>
  );
};
